/**
 * EmbedRoomCommands — Phase 1B-I follow-up.
 *
 * Mounted inside <LiveKitRoom> (specifically in OperatorRoom). Routes
 * the LiveKit-context commands the outer `useEmbedHostBridge` forwards
 * via `registerLivekitCommandHandler`:
 *
 *   • request-snapshot  → sends capture.snapshot to the field via the
 *                         CommandBus, same path as SnapshotButton.
 *   • open-whiteboard   → flips useWhiteboardPanelStore.open=true; the
 *                         existing operator panel mounts + broadcasts
 *                         whiteboard/opened so the field auto-opens.
 *   • close-whiteboard  → flips the store to false.
 *   • switch-camera     → sends camera.switch with facing="toggle".
 *   • mute              → setMicrophoneEnabled(false) on the operator.
 *   • unmute            → setMicrophoneEnabled(true).
 *
 * Errors → postToHost({ type: "error", code }) so the host SDK's
 * onError fires with a readable reason.
 *
 * This component is intentionally a leaf — it renders nothing, just
 * registers a handler in useEffect. The outer hook in SessionPage
 * passes commands to dispatchToLivekit; the dispatcher walks
 * livekitHandlers; first true return wins.
 *
 * Field-participant targeting (backlog §9.4, 2026-09-08): this bridge
 * is host-driven — there's no operator-facing UI here to hang a
 * `FieldParticipantPicker` off, and `HostCommand` carries no target
 * identity for an embedding integrator to specify one (a real gap if
 * an integrator ever needs multi-party targeting — extending
 * `HostCommand` with an optional participant id is the natural
 * follow-up, but is its own scoped change against a documented
 * customer-facing contract). What IS fixed here: `useFieldParticipants`
 * resolves the default target via a stable "who joined first" sort
 * instead of the old singleton `.find()`, which returned whatever
 * order LiveKit's `useParticipants()` happened to report and could
 * silently retarget when a 3rd field participant joined.
 */
import {
  useLocalParticipant,
  useParticipants,
  useRoomContext,
} from "@livekit/components-react";
import { useEffect } from "react";

import {
  CommandBus,
  type EnvelopeIdentity,
  makeLiveKitTransport,
} from "@/lib/commandBus";
import {
  postToHost,
  registerLivekitCommandHandler,
  type HostCommand,
} from "@/lib/embedHost";
import { useFieldParticipants } from "@/lib/fieldParticipants";
import { useWhiteboardPanelStore } from "@/lib/whiteboardPanelStore";


type Props = { sessionId: string };


export function EmbedRoomCommands({ sessionId }: Props) {
  const room = useRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  // Deterministic default: whoever joined first among the room's
  // field-role remote participants (Egress/Ingress/SIP/Agent bots
  // excluded — addressing one would time out as "command failed").
  // See the module docstring for why there's no picker here.
  const fieldParticipant = useFieldParticipants(participants)[0] ?? null;

  useEffect(() => {
    if (!room) return;
    if (typeof window === "undefined" || window.parent === window) {
      // Not embedded — nothing to register; SessionPage's outer hook
      // already short-circuits.
      return;
    }

    const transport = makeLiveKitTransport(room, sessionId);
    const self: EnvelopeIdentity = {
      kind: "user",
      id: room.localParticipant.identity,
    };
    const bus = new CommandBus(transport, sessionId, self);

    const reportError = (code: string, message: string): void => {
      postToHost({ source: "nb-iframe", type: "error", message, code });
    };

    const handle = (cmd: HostCommand): boolean => {
      switch (cmd.type) {
        case "open-whiteboard":
          useWhiteboardPanelStore.getState().setOpen(true);
          return true;
        case "close-whiteboard":
          useWhiteboardPanelStore.getState().setOpen(false);
          return true;
        case "mute":
          void localParticipant
            ?.setMicrophoneEnabled(false)
            .catch((e: Error) => reportError("mute-failed", e.message));
          return true;
        case "unmute":
          void localParticipant
            ?.setMicrophoneEnabled(true)
            .catch((e: Error) => reportError("unmute-failed", e.message));
          return true;
        case "switch-camera": {
          if (!fieldParticipant) {
            reportError("switch-camera-failed", "field user not in room");
            return true;
          }
          void bus
            .request({
              topic: "command",
              type: "camera.switch",
              payload: { facing: "toggle" },
              to: { kind: "participant", id: fieldParticipant.identity },
              to_identities: [fieldParticipant.identity],
              timeout_ms: 8_000,
            })
            .catch((e: Error) =>
              reportError("switch-camera-failed", e.message),
            );
          return true;
        }
        case "request-snapshot": {
          if (!fieldParticipant) {
            reportError(
              "request-snapshot-failed",
              "field user not in room",
            );
            return true;
          }
          void bus
            .request<unknown, {
              ok: boolean;
              evidence_id?: string;
              error?: string;
            }>({
              topic: "command",
              type: "capture.snapshot",
              payload: { kind: "snapshot", mime: "image/png" },
              to: { kind: "participant", id: fieldParticipant.identity },
              to_identities: [fieldParticipant.identity],
              timeout_ms: 30_000,
            })
            .then((ack) => {
              if (!ack?.ok) {
                reportError(
                  "request-snapshot-failed",
                  ack?.error ?? "field declined to capture",
                );
              }
            })
            .catch((e: Error) =>
              reportError("request-snapshot-failed", e.message),
            );
          return true;
        }
        default:
          return false;
      }
    };

    const unregister = registerLivekitCommandHandler(handle);
    return () => {
      unregister();
      bus.dispose();
    };
  }, [room, sessionId, localParticipant, fieldParticipant]);

  return null;
}
